import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  FileJson,
  ChevronLeft,
  ChevronRight,
  Upload,
  Eye,
  Trash2,
  RefreshCw,
  X,
  File,
  CheckCircle,
  AlertCircle,
  Clock,
  FileText,
  Search,
} from "lucide-react";
import "./GFSWorkOrdersList.css";

// Form types config
const FORM_TYPES_CONFIG = {
  aof_individual: { name: "AOF - Individual", color: "#3b82f6", bgColor: "#dbeafe" },
  aof_corporate: { name: "AOF - Corporate", color: "#8b5cf6", bgColor: "#ede9fe" },
  sa_individual: { name: "SA - Individual", color: "#10b981", bgColor: "#d1fae5" },
  sa_corporate: { name: "SA - Corporate", color: "#f59e0b", bgColor: "#fef3c7" },
  redemption: { name: "Redemption (RP)", color: "#ef4444", bgColor: "#fee2e2" },
  additional_purchase: { name: "Additional Purchase (IUTA/AI)", color: "#06b6d4", bgColor: "#cffafe" },
};

// Custom Dropdown Component
const CustomDropdown = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="gfs-custom-dropdown" ref={dropdownRef}>
      <button className="gfs-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedOption?.label || placeholder}</span>
        <svg className={`dropdown-arrow ${isOpen ? "open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {isOpen && (
        <div className="gfs-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              className={`gfs-dropdown-option ${value === option.value ? "selected" : ""}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              {option.label}
              {value === option.value && <span className="check-mark">&#10003;</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [formType, setFormType] = useState("aof_individual");
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const formTypeOptions = [
    { value: "aof_individual", label: "AOF - Individual" },
    { value: "aof_corporate", label: "AOF - Corporate" },
    { value: "sa_individual", label: "SA - Individual" },
    { value: "sa_corporate", label: "SA - Corporate" },
    { value: "redemption", label: "Redemption (RP)" },
    { value: "additional_purchase", label: "Additional Purchase (IUTA/AI)" },
  ];

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(f => 
      f.name.toLowerCase().match(/\.(pdf|tiff?|jpe?g|png)$/i)
    );
    const existingNames = files.map(f => f.name);
    const uniqueFiles = validFiles.filter(f => !existingNames.includes(f.name));
    
    if (validFiles.length !== newFiles.length) {
      alert('Only PDF, TIFF, JPG, and PNG files are supported. Invalid files were skipped.');
    }
    
    setFiles(prev => [...prev, ...uniqueFiles]);
    const newStatus = {};
    uniqueFiles.forEach(f => { newStatus[f.name] = 'pending'; });
    setUploadStatus(prev => ({ ...prev, ...newStatus }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    dropZoneRef.current?.classList.add('drag-over');
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    dropZoneRef.current?.classList.remove('drag-over');
  };

  const removeFile = (filename) => {
    setFiles(prev => prev.filter(f => f.name !== filename));
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[filename];
      return newStatus;
    });
  };

  const handleUploadAll = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    const results = [];
    const token = localStorage.getItem("token");

    for (const file of files) {
      setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
      
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("form_type", formType);
        
        const res = await fetch("/v1/gfs/api/extract", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        
        const data = await res.json();
        if (res.ok) {
          setUploadStatus(prev => ({ ...prev, [file.name]: 'success' }));
          results.push({ filename: file.name, success: true, work_order_id: data.work_order?.id });
        } else {
          setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
          results.push({ filename: file.name, success: false, error: data.message || "Upload failed" });
        }
      } catch (err) {
        setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
        results.push({ filename: file.name, success: false, error: err.message });
      }
    }

    setUploadResults(results);
    setUploading(false);
    if (onUploadComplete) onUploadComplete(results);
  };

  const handleClose = () => {
    setFiles([]);
    setUploadStatus({});
    setUploadResults([]);
    setFormType("aof_individual");
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading': return <RefreshCw size={16} className="spin" style={{ color: '#3b82f6' }} />;
      case 'success': return <CheckCircle size={16} style={{ color: '#22c55e' }} />;
      case 'error': return <AlertCircle size={16} style={{ color: '#ef4444' }} />;
      default: return <Clock size={16} style={{ color: '#9ca3af' }} />;
    }
  };

  if (!isOpen) return null;

  const allUploaded = files.length > 0 && files.every(f => uploadStatus[f.name] === 'success' || uploadStatus[f.name] === 'error');
  const successCount = Object.values(uploadStatus).filter(s => s === 'success').length;
  const errorCount = Object.values(uploadStatus).filter(s => s === 'error').length;

  return (
    <div className="gfs-modal-overlay" onClick={handleClose}>
      <div className="gfs-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="gfs-modal-header">
          <h3><Upload size={20} /> Upload & Extract</h3>
          <button className="gfs-modal-close" onClick={handleClose}><X size={20} /></button>
        </div>

        <div className="gfs-modal-body">
          {/* Form Type Selection */}
          <div className="gfs-form-type-select">
            <label>Form Type</label>
            <select value={formType} onChange={(e) => setFormType(e.target.value)} disabled={uploading}>
              {formTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Drop Zone */}
          <div
            ref={dropZoneRef}
            className="gfs-drop-zone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={48} />
            <p>Drag & drop files here</p>
            <span>or click to browse (PDF, TIFF, JPG, PNG)</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="gfs-file-list">
              <div className="gfs-file-list-header">
                <span>{files.length} file(s) selected</span>
                {allUploaded && (
                  <span className="gfs-upload-summary">
                    {successCount} success, {errorCount} failed
                  </span>
                )}
              </div>
              <div className="gfs-file-items">
                {files.map(file => (
                  <div key={file.name} className={`gfs-file-item ${uploadStatus[file.name]}`}>
                    <div className="gfs-file-info">
                      <File size={16} />
                      <span className="gfs-file-name" title={file.name}>
                        {file.name.length > 35 ? file.name.substring(0, 35) + '...' : file.name}
                      </span>
                      <span className="gfs-file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    {uploadStatus[file.name] === 'error' && uploadResults.find(r => r.filename === file.name)?.error && (
                      <div className="gfs-file-error">
                        {uploadResults.find(r => r.filename === file.name)?.error}
                      </div>
                    )}
                    <div className="gfs-file-actions">
                      {getStatusIcon(uploadStatus[file.name])}
                      {uploadStatus[file.name] === 'pending' && !uploading && (
                        <button className="gfs-file-remove" onClick={() => removeFile(file.name)}>
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="gfs-modal-footer">
          <button className="gfs-btn-cancel" onClick={handleClose}>
            {allUploaded ? 'Close' : 'Cancel'}
          </button>
          {!allUploaded && (
            <button
              className="gfs-btn-upload-all"
              onClick={handleUploadAll}
              disabled={files.length === 0 || uploading}
            >
              {uploading ? (
                <><RefreshCw size={16} className="spin" /> Uploading...</>
              ) : (
                <><Upload size={16} /> Upload {files.length} File{files.length !== 1 ? 's' : ''}</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const GFSWorkOrdersList = () => {
  const navigate = useNavigate();
  const { refreshUsage } = useOutletContext() || {};

  const [workOrders, setWorkOrders] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1, limit: 15, skip: 0 });
  const [stats, setStats] = useState({ total: 0, completed: 0, processing: 0, pending: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formTypeFilter, setFormTypeFilter] = useState("all");
  const [error, setError] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // Debounce timer ref for search
  const searchTimerRef = useRef(null);

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];

  const formTypeOptions = [
    { value: "all", label: "All Form Types" },
    { value: "aof_individual", label: "AOF - Individual" },
    { value: "aof_corporate", label: "AOF - Corporate" },
    { value: "sa_individual", label: "SA - Individual" },
    { value: "sa_corporate", label: "SA - Corporate" },
    { value: "redemption", label: "Redemption (RP)" },
    { value: "additional_investment", label: "Additional Investment" },
  ];

  useEffect(() => {
    fetchData();
  }, [statusFilter, formTypeFilter]);

  const fetchData = async (skip = 0, search = searchTerm) => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");

      const params = new URLSearchParams({ skip: skip.toString(), limit: "15" });
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (formTypeFilter !== "all") params.append("form_type", formTypeFilter);
      if (search && search.trim()) params.append("search", search.trim());

      const [ordersRes, statsRes] = await Promise.all([
        fetch(`/v1/gfs/api/work-orders?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch("/v1/gfs/api/stats", { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (ordersRes.ok) {
        const data = await ordersRes.json();
        setWorkOrders(data.work_orders || []);
        setPagination({
          total: data.total || 0,
          page: Math.floor(skip / 15) + 1,
          pages: Math.ceil((data.total || 0) / 15),
          limit: 15,
          skip: skip,
        });
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats({
          total: statsData.total_work_orders || 0,
          completed: statsData.by_status?.completed || 0,
          processing: statsData.by_status?.processing || 0,
          pending: statsData.by_status?.pending || 0,
          failed: statsData.by_status?.failed || 0,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear existing timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    
    // Debounce search - wait 500ms after user stops typing
    searchTimerRef.current = setTimeout(() => {
      fetchData(0, value);
    }, 500);
  };

  const handleUploadComplete = (results) => {
    fetchData(0);
    if (refreshUsage) refreshUsage();
    
    const successResults = results.filter(r => r.success);
    if (successResults.length === 1 && successResults[0].work_order_id) {
      setShowUploadModal(false);
      navigate(`/gfs/work-order/${successResults[0].work_order_id}`);
    }
  };

  const handleDelete = async (workOrderId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this work order?")) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/v1/gfs/api/work-orders/${workOrderId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchData(pagination.skip);
      } else {
        const data = await res.json();
        setError(`Delete failed: ${data.message || "Unknown error"}`);
      }
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) fetchData(pagination.skip + pagination.limit);
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) fetchData(Math.max(0, pagination.skip - pagination.limit));
  };

  // No need for local filtering anymore - backend handles search
  const filteredWorkOrders = workOrders;

  const getStatusBadgeClass = (status) => {
    const classes = { completed: "status-completed", processing: "status-processing", pending: "status-pending", failed: "status-failed" };
    return classes[status] || "status-pending";
  };

  const getFormTypeConfig = (formType) => FORM_TYPES_CONFIG[formType] || { name: formType, color: "#6b7280", bgColor: "#f3f4f6" };

  return (
    <div className="gfs-work-orders-list">
      {/* Header */}
      <div className="gfs-header-banner">
        <div className="gfs-header-info">
          <h2><FileText size={24} /> GFS Work Orders</h2>
          <p>Manulife Form Extractions - AOF, SA, Redemption, Additional Investment</p>
        </div>
        <div className="gfs-header-actions">
          <button className="gfs-btn-upload" onClick={() => setShowUploadModal(true)}>
            <Upload size={18} /> Upload & Extract
          </button>
        </div>
      </div>

      {error && (
        <div className="gfs-error-banner">
          {error}
          <button onClick={() => setError("")}>&times;</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="gfs-stats-grid">
        <div className="gfs-stat-card"><div className="gfs-stat-value">{stats.total}</div><div className="gfs-stat-label">Total</div></div>
        <div className="gfs-stat-card completed"><div className="gfs-stat-value">{stats.completed}</div><div className="gfs-stat-label">Completed</div></div>
        <div className="gfs-stat-card processing"><div className="gfs-stat-value">{stats.processing + stats.pending}</div><div className="gfs-stat-label">In Progress</div></div>
        <div className="gfs-stat-card failed"><div className="gfs-stat-value">{stats.failed}</div><div className="gfs-stat-label">Failed</div></div>
      </div>

      {/* Filters */}
      <div className="gfs-filters-row">
        <div className="gfs-search-wrapper">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by ID, filename, or UID..."
            className="gfs-search-input"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        <CustomDropdown value={statusFilter} onChange={setStatusFilter} options={statusOptions} placeholder="Status" />
        <CustomDropdown value={formTypeFilter} onChange={setFormTypeFilter} options={formTypeOptions} placeholder="Form Type" />
        <button className="gfs-btn-refresh" onClick={() => fetchData(0)}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="gfs-table-container">
        <table className="gfs-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Filename</th>
              <th>Form Type</th>
              <th>Status</th>
              <th>Pages</th>
              <th>Created</th>
              <th>Cost</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="gfs-table-loading"><RefreshCw size={20} className="spin" /> Loading...</td></tr>
            ) : filteredWorkOrders.length === 0 ? (
              <tr><td colSpan="8" className="gfs-table-empty">No work orders found. Upload a document to get started.</td></tr>
            ) : (
              filteredWorkOrders.map((wo) => {
                const formConfig = getFormTypeConfig(wo.form_type);
                return (
                  <tr key={wo._id} onClick={() => navigate(`/gfs/work-order/${wo._id}`)}>
                    <td className="gfs-id-cell">{wo._id?.slice(-8)}</td>
                    <td className="gfs-filename-cell" title={wo.filename}>
                      {wo.filename?.substring(0, 30) || "-"}{wo.filename?.length > 30 && "..."}
                    </td>
                    <td>
                      <span className="gfs-form-badge" style={{ backgroundColor: formConfig.bgColor, color: formConfig.color }}>
                        {formConfig.name}
                      </span>
                    </td>
                    <td><span className={`gfs-status-badge ${getStatusBadgeClass(wo.status)}`}>{wo.status}</span></td>
                    <td>{wo.total_pages || "-"}</td>
                    <td>{formatDate(wo.created_at)}</td>
                    <td>${wo.cost?.toFixed(4) || "0.0000"}</td>
                    <td className="gfs-actions-cell" onClick={(e) => e.stopPropagation()}>
                      <button className="gfs-action-btn view" title="View Details" onClick={() => navigate(`/gfs/work-order/${wo._id}`)}>
                        <Eye size={16} />
                      </button>
                      <button className="gfs-action-btn json" title="Download JSON" onClick={(e) => {
                        e.stopPropagation();
                        const data = wo.extracted_data || {};
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a"); a.href = url;
                        a.download = `${wo.filename?.replace(/\.[^.]+$/, "") || wo._id}.json`;
                        a.click();
                      }}>
                        <FileJson size={16} />
                      </button>
                      <button className="gfs-action-btn delete" title="Delete" onClick={(e) => handleDelete(wo._id, e)}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="gfs-pagination">
        <div className="gfs-pagination-info">
          Showing {workOrders.length > 0 ? pagination.skip + 1 : 0} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
        </div>
        <div className="gfs-pagination-controls">
          <button onClick={handlePrevPage} disabled={pagination.page <= 1}><ChevronLeft size={16} /> Previous</button>
          <span className="gfs-page-indicator">Page {pagination.page} of {pagination.pages || 1}</span>
          <button onClick={handleNextPage} disabled={pagination.page >= pagination.pages}>Next <ChevronRight size={16} /></button>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
};

export default GFSWorkOrdersList;
